import React, { SyntheticEvent, useMemo, useState } from "react";
import { useAsyncValue, useFetcher, useLoaderData, Await } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Stack } from "@mui/system";
import { format } from "date-fns";

import FileInput from "components/FileInput";
import { DatabasesEmailing, GetCampaignDetailsResponse, GetDWHSegmentsListResponse, SegmentListItem } from "services/apiService";
import { Autocomplete } from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import { ToastContainer } from "react-toastify";

export enum AddressBookFormAction {
    EmptyContacts = "EMPTY_CONTACTS",
    AddContacts = "ADD_CONTACTS",
    CreateDWHSegment = "CREATE_DWH_SEGMENT",
    ChooseDWHSegment = "CHOOSE_DWH_SEGMENT",
    UploadDynamicContacts = "UPLOAD_DYNAMIC_CONTACTS",
};

const EmptyContactsForm = ({
    campaign,
    expanded,
    handleExpandedChange,
}: {
    campaign: GetCampaignDetailsResponse;
    expanded: string | false;
    handleExpandedChange: (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => void;
}) => {
    const fetcher = useFetcher();
    const inputDisabled = fetcher.state === "submitting";

    return (
        <Grid item xs={12} component={fetcher.Form} method="delete">
            <input type="hidden" name="action" value={AddressBookFormAction.EmptyContacts}/>
            <Accordion
                expanded={expanded === AddressBookFormAction.EmptyContacts}
                onChange={handleExpandedChange(AddressBookFormAction.EmptyContacts)}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Empty contacts</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8} display="flex" alignItems="center">
                            Be careful, the following button will empty all the contact in
                            this campaign's address book!
                        </Grid>

                        <input type="hidden" name="cm-group-id" value={campaign.cm_group_id} />
                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                sx={{ height: "100%" }}
                                type="submit"
                                variant="contained"
                                startIcon={!inputDisabled && <DeleteIcon />}
                                color="error"
                                disabled={inputDisabled}
                            >
                                <Box sx={{ height: 24, display: "flex", justifyContent: "center", alignItems: "center"}}>
                                    {inputDisabled ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        "Empty contacts"
                                    )}
                                </Box>
                            </Button>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

// ========================================== upload static contacts csv files =================================
const UploadContactsForm = ({
    campaign,
    expanded,
    handleExpandedChange,
    action,
    title,
}: {
    campaign: GetCampaignDetailsResponse;
    expanded: string | false;
    handleExpandedChange: (
        panel: string
    ) => (event: SyntheticEvent, isExpanded: boolean) => void;
    action: AddressBookFormAction;
    title: string;
}) => {
    const fetcher = useFetcher();
    const inputDisabled = fetcher.state === "submitting";

    return (
        <Grid
            item
            xs={12}
            component={fetcher.Form}
            method="post"
            encType="multipart/form-data"
        >
            <input type="hidden" name="action" value={action} />
            <input type="hidden" name="cm-group-id" value={campaign.cm_group_id} />

            <Accordion expanded={expanded === action} onChange={handleExpandedChange(action)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <span>{title}</span>
                </AccordionSummary>

                {/* <ToastContainer /> */}
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <FileInput
                                name="file"
                                required
                                disabled={inputDisabled}
                                size="small"
                                inputProps={{ accept: "text/csv" }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4} display="flex" alignItems="center">
                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                endIcon={!inputDisabled && <FileUploadIcon />}
                                disabled={inputDisabled}
                            >
                                <Box
                                    sx={{
                                        height: 24,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    {inputDisabled ? <CircularProgress size={16} /> : "Upload contacts"}
                                </Box>
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};


// ========================================== upload dynamic contacts csv informations =========================================
const CSVContactsUploadForm = ({
    campaign,
    expanded,
    handleExpandedChange,
}: {
    campaign: GetCampaignDetailsResponse;
    expanded: string | false;
    handleExpandedChange: (
        panel: string
    ) => (event: SyntheticEvent, isExpanded: boolean) => void;
}) => (
    <UploadContactsForm
        campaign={campaign}
        expanded={expanded}
        handleExpandedChange={handleExpandedChange}
        action={AddressBookFormAction.AddContacts}
        title="Upload contacts via a CSV file (header: mobile only)"
    />
);

const UploadDynamicContactsCSV = ({
    campaign,
    expanded,
    handleExpandedChange,
}: {
    campaign: GetCampaignDetailsResponse;
    expanded: string | false;
    handleExpandedChange: (
        panel: string
    ) => (event: SyntheticEvent, isExpanded: boolean) => void;
}) => (
    <UploadContactsForm
        campaign={campaign}
        expanded={expanded}
        handleExpandedChange={handleExpandedChange}
        action={AddressBookFormAction.UploadDynamicContacts}
        title="Upload dynamic contact via CSV (headers: email, mobile, firstname, lastname, zipcode, birthdate, civility, city, address_postal)"
    />
);


const DWHSegmentCreationForm = ({
    campaign,
    emailingDatabases,
    expanded,
    handleExpandedChange,
}: {
    campaign: GetCampaignDetailsResponse;
    emailingDatabases: DatabasesEmailing[];
    expanded: string | false;
    handleExpandedChange: (
        panel: string
    ) => (event: SyntheticEvent, isExpanded: boolean) => void;
}) => {
    const fetcher = useFetcher();
    const inputDisabled = fetcher.state === "submitting";
    const [emailingDatabase, setEmailingDatabase] = useState<DatabasesEmailing | null>(emailingDatabases[0]);

    const shootDate = useMemo(
        () =>
            format(new Date(campaign.stats_scheduled_date), "yyyy-MM-dd HH:mm:ss"),
        [campaign.stats_scheduled_date]
    );

    const [showGenderAgeInputs, setShowGenderAgeInputs] = useState(false);
    const [showCspInput, setShowCspInput] = useState(false);
    const [showScoreInputs, setShowScoreInputs] = useState(false);

    return (
        <Grid item xs={12} component={fetcher.Form} method="post">
            <input
                type="hidden"
                name="action"
                value={AddressBookFormAction.CreateDWHSegment}
            />

            {/* Campaign information */}
            <input type="hidden" name="stat_base_id" value={emailingDatabase?.id} />
            {/* <input type="hidden" name="stat_base_id" value={86} /> */}
            <input type="hidden" name="country" value={campaign.stats_country_name} />
            {/* <input type="hidden" name="tag" value={campaign.stats_tags_name} /> */}
            <input type="hidden" name="tag" value={campaign.stats_dwtag_name} />
            <input
                type="hidden"
                name="advertiser"
                value={campaign.stats_advertiser_name}
            />
            <input type="hidden" name="campaign" value={campaign.name} />
            <input type="hidden" name="router_list_id" value={campaign.cm_group_id} />
            <input type="hidden" name="shoot_date" value={shootDate} />

            <Accordion
                expanded={expanded === AddressBookFormAction.CreateDWHSegment}
                onChange={handleExpandedChange(AddressBookFormAction.CreateDWHSegment)}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Create DWH Segment</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container spacing={4}>
                        {/* Success message */}
                        {fetcher.data === true && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    DWH segment creation is in progress!
                                </Alert>
                            </Grid>
                        )}
                        {/* Error alert */}
                        {fetcher.state === "idle" && fetcher.data && fetcher.data.error && (
                            <Grid item xs={12}>
                                <Alert severity="error">{fetcher.data.error}</Alert>
                            </Grid>
                        )}
                        <input
                            type="hidden"
                            name="cm-group-id"
                            value={campaign.cm_group_id}
                        />
                        {/* Mandatory Fields */}
                        <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12}>
                                <Divider>Mandatory Fields</Divider>
                            </Grid>

                            {/* Marketing Pression Days */}
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    value={emailingDatabase}
                                    onChange={(_, newValue) => {
                                        setEmailingDatabase(newValue);
                                    }}
                                    disablePortal
                                    options={emailingDatabases}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(option) => option.base}
                                    renderInput={(params) => (
                                        <TextField required {...params} label="Database" size="small" />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            {option.id} - {option.base}
                                        </li>
                                    )}
                                />
                            </Grid>

                            {/* Marketing Pression Days */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    type="number"
                                    name="marketing_pression_days"
                                    label="Marketing Pression Days"
                                    inputProps={{ min: 0 }}
                                    disabled={inputDisabled}
                                    required
                                />
                            </Grid>

                            {/* Max Volume */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    type="number"
                                    name="max_volume"
                                    label="Max Volume"
                                    inputProps={{ min: 0 }}
                                    disabled={inputDisabled}
                                    required
                                />
                            </Grid>
                        </Grid>

                        {/* Optional Fields */}
                        <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12}>
                                <Divider>Optional Fields</Divider>
                            </Grid>

                            {/* Form toggles */}
                            <Grid item xs={12} md={3}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showGenderAgeInputs}
                                                onChange={(event) =>
                                                    setShowGenderAgeInputs(event.target.checked)
                                                }
                                            />
                                        }
                                        label="Gender & Age"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showCspInput}
                                                onChange={(event) =>
                                                    setShowCspInput(event.target.checked)
                                                }
                                            />
                                        }
                                        label="CSP"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showScoreInputs}
                                                onChange={(event) =>
                                                    setShowScoreInputs(event.target.checked)
                                                }
                                            />
                                        }
                                        label="Scores"
                                    />
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12} md={9} container spacing={2}>
                                {/* Gender & Age Classes inputs */}
                                {showGenderAgeInputs && (
                                    <Grid item xs={12} container spacing={2}>
                                        {/* Gender select */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="genderMultiSelect">Gender</InputLabel>
                                                <Select
                                                    labelId="genderMultiSelect"
                                                    id="genderMultiSelect"
                                                    label="Gender"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="gender"
                                                >
                                                    <MenuItem value="M">Male</MenuItem>
                                                    <MenuItem value="F">Female</MenuItem>
                                                    <MenuItem value="O">Other</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Age classes select */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="ageClassesMultiSelect">
                                                    Age Classes
                                                </InputLabel>
                                                <Select
                                                    labelId="ageClassesMultiSelect"
                                                    id="ageClassesMultiSelect"
                                                    label="Age Classes"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="age_class"
                                                >
                                                    <MenuItem value="18">18</MenuItem>
                                                    <MenuItem value="18-25">18-25</MenuItem>
                                                    <MenuItem value="26-35">26-35</MenuItem>
                                                    <MenuItem value="36-45">36-45</MenuItem>
                                                    <MenuItem value="46-55">46-55</MenuItem>
                                                    <MenuItem value="56-65">56-65</MenuItem>
                                                    <MenuItem value="65">65</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )}

                                {/* CSP input */}
                                {showCspInput && (
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="cspMultiSelect">CSP</InputLabel>
                                                <Select
                                                    labelId="cspMultiSelect"
                                                    id="cspMultiSelect"
                                                    label="CSP"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="csp"
                                                >
                                                    <MenuItem value="0">0</MenuItem>
                                                    <MenuItem value="1">1</MenuItem>
                                                    <MenuItem value="2">2</MenuItem>
                                                    <MenuItem value="3">3</MenuItem>
                                                    <MenuItem value="4">4</MenuItem>
                                                    <MenuItem value="5">5</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )}

                                {/* Score inputs */}
                                {showScoreInputs && (
                                    <Grid item xs={12} container spacing={2}>
                                        {/* Score of landlords */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="scoreOfLandlordsMultiSelect">
                                                    Score Of Landlords
                                                </InputLabel>
                                                <Select
                                                    labelId="scoreOfLandlordsMultiSelect"
                                                    id="scoreOfLandlordsMultiSelect"
                                                    label="Score Of Landlords"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="score_of_landlords"
                                                >
                                                    <MenuItem value="0">0</MenuItem>
                                                    <MenuItem value="1">1</MenuItem>
                                                    <MenuItem value="2">2</MenuItem>
                                                    <MenuItem value="3">3</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Score of individual houses */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="scoreOfIndividualHousesMultiSelect">
                                                    Score Of Individual Houses
                                                </InputLabel>
                                                <Select
                                                    labelId="scoreOfIndividualHousesMultiSelect"
                                                    id="scoreOfIndividualHousesMultiSelect"
                                                    label="Score Of Individual Houses"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="score_of_individual_houses"
                                                >
                                                    <MenuItem value="0">0</MenuItem>
                                                    <MenuItem value="1">1</MenuItem>
                                                    <MenuItem value="2">2</MenuItem>
                                                    <MenuItem value="3">3</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Score of tax households */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="scoreOfTaxHouseholdsMultiSelect">
                                                    Score Of Tax Households
                                                </InputLabel>
                                                <Select
                                                    labelId="scoreOfTaxHouseholdsMultiSelect"
                                                    id="scoreOfTaxHouseholdsMultiSelect"
                                                    label="Score Of Tax Households"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="score_of_tax_households"
                                                >
                                                    <MenuItem value="0">0</MenuItem>
                                                    <MenuItem value="1">1</MenuItem>
                                                    <MenuItem value="2">2</MenuItem>
                                                    <MenuItem value="3">3</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Poverty score */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="povertyScoreMultiSelect">
                                                    Poverty Score
                                                </InputLabel>
                                                <Select
                                                    labelId="povertyScoreMultiSelect"
                                                    id="povertyScoreMultiSelect"
                                                    label="Poverty Score"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="poverty_score"
                                                >
                                                    <MenuItem value="0">0</MenuItem>
                                                    <MenuItem value="1">1</MenuItem>
                                                    <MenuItem value="2">2</MenuItem>
                                                    <MenuItem value="3">3</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Score median income */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="scoreMedianIncomeMultiSelect">
                                                    Score Median Income
                                                </InputLabel>
                                                <Select
                                                    labelId="scoreMedianIncomeMultiSelect"
                                                    id="scoreMedianIncomeMultiSelect"
                                                    label="Score Median Income"
                                                    multiple
                                                    defaultValue={[]}
                                                    name="score_median_income"
                                                >
                                                    <MenuItem value="0">0</MenuItem>
                                                    <MenuItem value="1">1</MenuItem>
                                                    <MenuItem value="2">2</MenuItem>
                                                    <MenuItem value="3">3</MenuItem>
                                                    <MenuItem value="4">4</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        {/* Submit form */}
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={inputDisabled}
                            >
                                <Box
                                    sx={{
                                        height: 24,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    {inputDisabled ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        "Create DWH Segment"
                                    )}
                                </Box>
                            </Button>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

const DWHSegmentSelectForm = (
    { dwhSegments,
        campaign }: {
            dwhSegments: SegmentListItem[],
            campaign: GetCampaignDetailsResponse;
        }) => {
    const fetcher = useFetcher();
    const [dwhSegment, setDwhSegment] = useState<SegmentListItem | null>(dwhSegments[0]);

    return (
        <Grid item xs={12} component={fetcher.Form} method="put" id="dwhSegmentSelectForm">
            <input
                type="hidden"
                name="action"
                value={AddressBookFormAction.ChooseDWHSegment}
            />
            <input type="hidden" name="campaign-id" value={campaign?.id} />
            <input type="hidden" name="cm-campaign-id" value={campaign?.cm_id} />
            <input type="hidden" name="cm-group-id" value={dwhSegment?.router_list_id} />

            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <Typography>Choose existant DWH Segment</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid item xs={12} container spacing={2}>
                        {fetcher.data === true && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    Update campaing addressBook success !
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12} md={8}>
                            <Autocomplete
                                value={dwhSegment}
                                onChange={(_, newValue) => {
                                    setDwhSegment(newValue); console.log('dwhSegment : ', dwhSegment)
                                }}
                                disablePortal
                                options={dwhSegments}
                                isOptionEqualToValue={(option, value) => option.dwh_segment_id === value.dwh_segment_id}
                                getOptionLabel={(option) => option.campaign}
                                renderInput={(params) => (
                                    <TextField required {...params} label="DWH Segment" size="small" />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.dwh_segment_id}>
                                        {option.campaign}
                                    </li>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <LoadingButton
                                aria-label="save"
                                type="submit"
                                form="dwhSegmentSelectForm"
                                // startIcon={<DeleteIcon />}
                                loading={fetcher.state === "submitting"}
                                variant="contained"
                                color="primary"
                                fullWidth
                            >
                                Save

                            </LoadingButton>
                        </Grid>

                    </Grid>

                </AccordionDetails>
            </Accordion>
        </Grid>
    )
}

const CampaignAddressBook = () => {
    const loaderData = useLoaderData() as {
        data: Promise<[GetCampaignDetailsResponse, DatabasesEmailing[], GetDWHSegmentsListResponse]>;
    };

    return (
        // Loading indicator while fetching stats data
        <React.Suspense
            fallback={
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress />
                    <p>Loading campaigns and addressBooks data...</p>
                </Box>
            }
        >
            <Await
                resolve={loaderData.data}
                errorElement={<p>Error while loading campaigns or addressBooks data</p>}
            >
                <CampaignAddressBookFrom />
            </Await>
        </React.Suspense>

    );
}

const CampaignAddressBookFrom = () => {
    const [
        campaign,
        emailingDatabases,
        dwhSegments
    ] = useAsyncValue() as [
        GetCampaignDetailsResponse,
        DatabasesEmailing[],
        GetDWHSegmentsListResponse
    ];
    const listSegment = useMemo(
        () => dwhSegments.list_segment,
        [dwhSegments]
    )
    console.log("\n\n---- campaing : ", campaign.name)
    console.log("\n\n---- emailingDatabases : ", emailingDatabases)
    console.log("\n\n---- listSegment : ", listSegment)
    // console.log("\n\n---- dwhSegments : ", dwhSegments)
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const handleExpandedChange =
        (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    return (
        <>
        <ToastContainer />
            <Stack spacing={4} sx={{ mb: 4 }}>
                <Typography variant="h4" >
                    <span style={{ color: "grey" }}>{"Campaing AddressBook > "}</span>
                    <span>{campaign.name}</span>
                </Typography>
            </Stack>

            <Grid container spacing={4}>


                {/* Empty contacts */}
                <EmptyContactsForm
                    campaign={campaign}
                    expanded={expanded}
                    handleExpandedChange={handleExpandedChange}
                />

                {/* ================ Upload static contacts via a CSV sheet ================= */}
                <CSVContactsUploadForm
                    campaign={campaign}
                    expanded={expanded}
                    handleExpandedChange={handleExpandedChange}
                />

                {/* ======================== upload dynamic contact csv ======================== */}
                <UploadDynamicContactsCSV
                    campaign={campaign}
                    expanded={expanded}
                    handleExpandedChange={handleExpandedChange}
                />

                {/* Add contacts via request to DWH */}
                <DWHSegmentCreationForm
                    campaign={campaign}
                    emailingDatabases={emailingDatabases}
                    expanded={expanded}
                    handleExpandedChange={handleExpandedChange}
                />

                <DWHSegmentSelectForm
                    campaign={campaign}
                    dwhSegments={listSegment} />
            </Grid>
        </>

    );
};

export default CampaignAddressBook;
